import { useEffect, useCallback, useRef } from 'react';
import { trackEvent, trackClick as trackClickEvent, trackLinkClick } from '@/lib/posthog';
import {
    getElementInfo,
    getPageContext,
    isOutboundLink,
    isDownloadLink,
    getScrollDepth,
    throttle,
    getFileName,
    getFileExtension
} from '@/lib/posthog-helpers';

export interface PostHogInteractionTrackingOptions {
    /**
     * Enable automatic click tracking
     */
    trackClicks?: boolean;

    /**
     * Enable automatic scroll depth tracking
     */
    trackScrollDepth?: boolean;

    /**
     * Scroll depth milestones to track (default: [25, 50, 75, 100])
     */
    scrollMilestones?: number[];

    /**
     * CSS selector for elements to track clicks on
     */
    clickSelectors?: string;

    /**
     * Track outbound links automatically
     */
    trackOutbound?: boolean;

    /**
     * Track file downloads automatically
     */
    trackDownloads?: boolean;

    /**
     * Enable page engagement time tracking
     */
    trackEngagementTime?: boolean;
}

/**
 * Hook for automatic interaction tracking (clicks, scroll depth, engagement time)
 *
 * Usage:
 * usePostHogInteractionTracking({
 *   trackClicks: true,
 *   trackScrollDepth: true,
 *   scrollMilestones: [25, 50, 75, 100]
 * });
 */
export const usePostHogInteractionTracking = (options: PostHogInteractionTrackingOptions = {}) => {
    const {
        trackClicks = true,
        trackScrollDepth = true,
        scrollMilestones = [25, 50, 75, 100],
        clickSelectors = 'a, button, [role="button"], [data-track]',
        trackOutbound = true,
        trackDownloads = true,
        trackEngagementTime = false,
    } = options;

    const scrollMilestonesTracked = useRef<Set<number>>(new Set());
    const engagementStartTime = useRef<number>(Date.now());
    const isEngaged = useRef<boolean>(true);

    /**
     * Track click events
     */
    const handleClick = useCallback((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const clickedElement = target.closest(clickSelectors) as HTMLElement;

        if (!clickedElement) return;

        const elementInfo = getElementInfo(clickedElement);

        // Check if it's a link
        if (clickedElement.tagName === 'A') {
            const href = clickedElement.getAttribute('href');
            if (!href) return;

            // Track downloads
            if (trackDownloads && isDownloadLink(href)) {
                const fileName = getFileName(href);
                const fileExtension = getFileExtension(href);

                trackEvent('download_started', {
                    ...elementInfo,
                    file_name: fileName,
                    file_extension: fileExtension,
                    file_url: href,
                    ...getPageContext(),
                });
                console.log('[PostHog] Download tracked:', fileName);
                return;
            }

            // Track outbound links
            if (trackOutbound && isOutboundLink(href)) {
                trackEvent('outbound_link_clicked', {
                    ...elementInfo,
                    link_url: href,
                    ...getPageContext(),
                });
                console.log('[PostHog] Outbound link tracked:', href);
                return;
            }

            // Track internal link clicks
            trackLinkClick({
                ...elementInfo,
                link_url: href,
                link_type: 'internal',
                ...getPageContext(),
            });
            return;
        }

        // Track button clicks
        if (clickedElement.tagName === 'BUTTON' || clickedElement.getAttribute('role') === 'button') {
            trackClickEvent({
                ...elementInfo,
                element_type: 'button',
                ...getPageContext(),
            });
            return;
        }

        // Track generic clicks on tracked elements
        trackClickEvent({
            ...elementInfo,
            ...getPageContext(),
        });
    }, [clickSelectors, trackOutbound, trackDownloads]);

    /**
     * Track scroll depth
     */
    const handleScroll = useCallback(
        throttle(() => {
            const scrollDepth = getScrollDepth();

            // Check if we've reached any new milestones
            scrollMilestones.forEach(milestone => {
                if (
                    scrollDepth >= milestone &&
                    !scrollMilestonesTracked.current.has(milestone)
                ) {
                    scrollMilestonesTracked.current.add(milestone);
                    console.log(`[PostHog] Scroll milestone ${milestone}% reached`);

                    trackEvent('scroll_milestone', {
                        scroll_depth: milestone,
                        scroll_depth_percentage: milestone,
                        ...getPageContext(),
                    });
                }
            });
        }, 500),
        [scrollMilestones]
    );

    /**
     * Reset scroll tracking on page change
     */
    const resetScrollTracking = useCallback(() => {
        scrollMilestonesTracked.current.clear();
        console.log('[PostHog] Scroll tracking reset');
    }, []);

    /**
     * Track page engagement time
     */
    const trackEngagement = useCallback(() => {
        if (!trackEngagementTime) return;

        const engagementTime = Math.round((Date.now() - engagementStartTime.current) / 1000);

        // Only track if user was engaged for more than 10 seconds
        if (engagementTime > 10) {
            trackEvent('page_engagement', {
                engagement_time_seconds: engagementTime,
                ...getPageContext(),
            });
            console.log('[PostHog] Page engagement time:', engagementTime, 'seconds');
        }
    }, [trackEngagementTime]);

    /**
     * Handle visibility change (tab switch, minimize)
     */
    const handleVisibilityChange = useCallback(() => {
        if (document.hidden) {
            isEngaged.current = false;
            trackEngagement();
        } else {
            isEngaged.current = true;
            engagementStartTime.current = Date.now();
        }
    }, [trackEngagement]);

    /**
     * Set up event listeners
     */
    useEffect(() => {
        if (typeof window === 'undefined') return;

        console.log('[PostHog] Interaction tracking initialized:', {
            trackClicks,
            trackScrollDepth,
            trackOutbound,
            trackDownloads,
            scrollMilestones,
        });

        // Click tracking
        if (trackClicks) {
            document.addEventListener('click', handleClick, true);
            console.log('[PostHog] Click tracking enabled');
        }

        // Scroll depth tracking
        if (trackScrollDepth) {
            window.addEventListener('scroll', handleScroll, { passive: true });
            console.log('[PostHog] Scroll depth tracking enabled, milestones:', scrollMilestones);
        }

        // Engagement time tracking
        if (trackEngagementTime) {
            document.addEventListener('visibilitychange', handleVisibilityChange);
            window.addEventListener('beforeunload', trackEngagement);
            console.log('[PostHog] Engagement time tracking enabled');
        }

        return () => {
            if (trackClicks) {
                document.removeEventListener('click', handleClick, true);
            }
            if (trackScrollDepth) {
                window.removeEventListener('scroll', handleScroll);
            }
            if (trackEngagementTime) {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                window.removeEventListener('beforeunload', trackEngagement);
            }
        };
    }, [
        trackClicks,
        trackScrollDepth,
        trackEngagementTime,
        scrollMilestones,
        trackOutbound,
        trackDownloads,
        handleClick,
        handleScroll,
        handleVisibilityChange,
        trackEngagement,
    ]);

    /**
     * Reset scroll tracking on route change
     */
    useEffect(() => {
        resetScrollTracking();
    }, [resetScrollTracking]);

    return {
        resetScrollTracking,
        trackEngagement,
    };
};

/**
 * Manual click tracking function for specific use cases
 */
export const trackPostHogClick = (
    element: HTMLElement,
    additionalParams?: Record<string, any>
) => {
    const elementInfo = getElementInfo(element);

    trackClickEvent({
        ...elementInfo,
        ...getPageContext(),
        ...additionalParams,
    });
};

/**
 * Manual scroll tracking function
 */
export const trackPostHogScroll = (additionalParams?: Record<string, any>) => {
    const scrollDepth = getScrollDepth();

    trackEvent('scroll_depth', {
        scroll_depth: scrollDepth,
        scroll_depth_percentage: scrollDepth,
        ...getPageContext(),
        ...additionalParams,
    });
};

/**
 * Manual download tracking function
 */
export const trackPostHogDownload = (
    fileUrl: string,
    additionalParams?: Record<string, any>
) => {
    const fileName = getFileName(fileUrl);
    const fileExtension = getFileExtension(fileUrl);

    trackEvent('download_started', {
        file_url: fileUrl,
        file_name: fileName,
        file_extension: fileExtension,
        ...getPageContext(),
        ...additionalParams,
    });
};

/**
 * Manual outbound link tracking function
 */
export const trackPostHogOutboundLink = (
    linkUrl: string,
    linkText?: string,
    additionalParams?: Record<string, any>
) => {
    trackEvent('outbound_link_clicked', {
        link_url: linkUrl,
        link_text: linkText,
        link_domain: new URL(linkUrl).hostname,
        ...getPageContext(),
        ...additionalParams,
    });
};
